import { Configuration } from '@api-client/configuration'
import { useEnvConfig } from '@contexts/EnvConfigProvider'
import { AxiosError, AxiosResponse } from 'axios'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ReturnType<T> {
	data: T | undefined
	loading: boolean
	error: boolean
	refetchData: () => Promise<void>
}

const createFederationApiUrlFromLocation = (
	location: typeof window.location
): string => {
	const { host, protocol } = location
	return `${protocol}//api.${host}`
}

export const useDataFederationApiUrl = () => {
	const envConfig = useEnvConfig()
	const envUrl = envConfig?.DATA_FEDERATION_API_URL
	return envUrl
		? new URL(envUrl, window.location.href).href
		: createFederationApiUrlFromLocation(window.location)
}

export const useDataFederationConfig = (
	config: Partial<Configuration> = {}
) => {
	const dataFederationApiUrl = useDataFederationApiUrl()
	return useMemo(
		() =>
			new Configuration({
				...config,
				basePath: dataFederationApiUrl.replace(/\/$/, ''),
			}),
		[config, dataFederationApiUrl]
	)
}

export const useApi = <T, Api>(
	fn: (api: Api) => Promise<AxiosResponse<T>>,
	condition: readonly unknown[],
	factory: (config?: Configuration) => Api
): ReturnType<T> => {
	const [data, setData] = useState<undefined | T>(undefined)
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setError] = useState<boolean>(false)
	const { enqueueSnackbar } = useSnackbar()
	const { t } = useTranslation()
	const dataFederationConfig = useDataFederationConfig()

	const isMounted = useRef(true)
	useEffect(() => {
		isMounted.current = true
		return () => {
			isMounted.current = false
		}
	}, [])

	const fetchData = useCallback(async () => {
		if (!isMounted.current) {
			return
		}
		setError(false)
		setLoading(true)

		try {
			const result = await fn(factory(dataFederationConfig))
			if (!isMounted.current) {
				return
			}
			setData(result.data)
		} catch (error) {
			if (!isMounted.current) {
				return
			}
			if (
				error &&
				error instanceof AxiosError &&
				error.code === 'ERR_NETWORK'
			) {
				enqueueSnackbar(t('toast.error.connectivity'), {
					variant: 'error',
				})
			}
			setError(true)
		}
		setLoading(false)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...condition, dataFederationConfig, factory, t, enqueueSnackbar])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	return { data, loading, error, refetchData: fetchData }
}
